/**
 * View Logs dashboard — pure helpers and log normalization.
 *
 * Shared in `src/utils` with other app-wide helpers.
 * Presentation stays in `ViewLogsDashboard.jsx`; rules stay here for review/tests.
 */

import { formatDate } from "date-fns";
import { enIN } from "date-fns/locale";
import { LOG_KEY } from "../pages/Candidate/Interview/interviewLogs";

/**
 * Same localStorage key as `addLog` / interview page (must stay in sync — use `LOG_KEY`).
 * Returns null when id is missing so we never read `interview_log_null`.
 */
export const getInterviewLogStorageKey = (interviewId) =>
  interviewId != null ? LOG_KEY(interviewId) : null;

export function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Maps API / stored rows into a single in-app shape.
 * Handles alternate field names from backend or legacy payloads.
 */
export function normalizeInterviewLogRow(row, interviewId) {
  const toIntOrNull = (v) => {
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
  };

  const normalizeIso = (v) => {
    if (!v) return "-";
    if (typeof v === "string") return v;
    try {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? "-" : d.toISOString();
    } catch {
      return "-";
    }
  };

  const eventNameRaw = row?.event_name ?? row?.eventName ?? row?.event_Name ?? row?.event ?? "-";
  const eventName = typeof eventNameRaw === "string" ? eventNameRaw.trim() : eventNameRaw;

  return {
    eventName,
    localTime: normalizeIso(row?.localTime),
    questionNum: toIntOrNull(row?.questionNum ?? row?.question ?? null),
    questionText: row?.questionText ?? row?.qusetionText ?? row?.QuestionText ?? row?.QusetionText ?? null,
    count: toIntOrNull(row?.count ?? null),
    timestamp: row?.timestamp ?? "-",
    totalQuestion: toIntOrNull(row?.totalQuestion ?? row?.totalQuestions ?? row?.totalQ ?? null),
    interviewId: row?.interviewId ?? interviewId ?? null,
  };
}

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function toFiniteNumberOrNull(v) {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** IST wall-clock display from `localTime` string (or parse fallback). */
export function formatIstTimeHHMMSSFromLocalTime(localTime) {
  if (!localTime || typeof localTime !== "string") return "-";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(localTime)) {
    return localTime.slice(11, 19);
  }
  try {
    const d = new Date(localTime);
    if (Number.isNaN(d.getTime())) return "-";
    const parts = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const h = parts.find((p) => p.type === "hour")?.value ?? "00";
    const m = parts.find((p) => p.type === "minute")?.value ?? "00";
    const s = parts.find((p) => p.type === "second")?.value ?? "00";
    return `${h}:${m}:${s}`;
  } catch {
    return "-";
  }
}

// --- Event type guards (align with logging `event_name` values) ---

export function isCameraIssueEvent(name) {
  return [
    "CAMERA_MIC_ACCESS_DENIED",
    "CAMERA_REVOKED_MID_INTERVIEW",
    "MIC_REVOKED_MID_INTERVIEW",
    "CAMERA_MIC_REVOKED_MID_INTERVIEW",
  ].includes(name);
}

export function isMidInterviewRevokeEvent(name) {
  return (
    name === "CAMERA_MIC_REVOKED_MID_INTERVIEW" ||
    name === "CAMERA_REVOKED_MID_INTERVIEW" ||
    name === "MIC_REVOKED_MID_INTERVIEW"
  );
}

export function isTabVisibilityEvent(name) {
  return name === "TAB_SWITCHED_AWAY" || name === "TAB_RETURNED";
}

export function isPageReloadEvent(name) {
  return name === "PAGE_RELOADED";
}

export function isReRecordEvent(name) {
  return name === "QUESTION_RE_RECORD_ATTEMPT" || name === "MAX_RE_RECORD_ATTEMPTS_EXCEEDED";
}

/** Browser history leave / return to interview (logged from Interview page). */
export function isBackNavigationEvent(name) {
  return (
    name === "BACK_NAVIGATION" ||
    name === "BACK_NAVIGATION_REOPENED" || // legacy
    name === "LINK_REOPENED"
  );
}

/** Whether an event row should be counted as a "flag" (unweighted, per row). */
export function isFlagEventName(name) {
  return (
    isCameraIssueEvent(name) ||
    isPageReloadEvent(name) ||
    isTabVisibilityEvent(name) ||
    isReRecordEvent(name) ||
    isBackNavigationEvent(name)
  );
}

/** Timeline dot color: blue = recording start, red = risk, green = other. */
export function getTimelineDotColorHex(eventName) {
  if (eventName === "QUESTION_RECORDING_STARTED") return "#2563eb";
  if (
    isPageReloadEvent(eventName) ||
    isReRecordEvent(eventName) ||
    isTabVisibilityEvent(eventName) ||
    isCameraIssueEvent(eventName) ||
    isBackNavigationEvent(eventName)
  ) {
    return "#ef4444";
  }
  return "#22c55e";
}

/**
 * Weight for per-question “flags” badge and global TOTAL FLAGS.
 * Matches the set of events shown as red on the timeline (excluding recording-started).
 */
export function getFlagWeightForEvent(eventName, count) {
  const n = toFiniteNumberOrNull(count);
  const w = n != null && n > 0 ? n : 1;
  if (isCameraIssueEvent(eventName)) return w;
  if (isPageReloadEvent(eventName)) return w;
  if (isTabVisibilityEvent(eventName)) return w;
  if (isReRecordEvent(eventName)) return w;
  if (isBackNavigationEvent(eventName)) return w;
  return 0;
}

/** Label for ×N chip on timeline rows (null = no chip). */
export function getEventCountChipLabel(eventName, count) {
  const n = toFiniteNumberOrNull(count);
  if (n == null || !Number.isFinite(n)) return null;
  if (isReRecordEvent(eventName)) return null;
  if (isMidInterviewRevokeEvent(eventName)) return `×${n}`;
  if (n <= 1) return null;
  return `×${n}`;
}

// --- Derived data builders (pure; used by the dashboard hook layer) ---

export function sortLogsByTimestampAsc(logs) {
  return [...logs].sort((a, b) => {
    const at = new Date(a.timestamp).getTime();
    const bt = new Date(b.timestamp).getTime();
    if (Number.isNaN(at) || Number.isNaN(bt)) return 0;
    return at - bt;
  });
}

/** Map question text → stable order (1-based) when `questionNum` is missing or zero. */
export function buildQuestionTextOrderMap(sortedLogs) {
  const map = new Map();
  let next = 1;
  sortedLogs.forEach((l) => {
    const t = typeof l.questionText === "string" ? l.questionText.trim() : "";
    if (!t) return;
    if (!map.has(t)) {
      map.set(t, next);
      next += 1;
    }
  });
  return map;
}

export function getQuestionGroupKey(log, questionTextOrderMap) {
  const qn = Number(log?.questionNum);
  if (Number.isFinite(qn) && qn > 0) return qn;
  const t = typeof log?.questionText === "string" ? log.questionText.trim() : "";
  // Special bucket for feedback-resume logs (questionNum intentionally 0).
  if (t === "Feedback") return -1;
  return t && questionTextOrderMap.has(t) ? questionTextOrderMap.get(t) : 0;
}

export function buildIntegritySummary(sortedLogs, questionTextOrderMap) {
  const totalEvents = sortedLogs.length;
  const totalQuestionsRaw =
    toFiniteNumberOrNull(
      sortedLogs.find((l) => typeof l.totalQuestion === "number" && l.totalQuestion > 0)?.totalQuestion,
    ) ??
    toFiniteNumberOrNull(sortedLogs.find((l) => l.totalQuestion != null && l.totalQuestion > 0)?.totalQuestion) ??
    null;
  const totalQuestions = totalQuestionsRaw ?? (questionTextOrderMap.size > 0 ? questionTextOrderMap.size : null);

  // KPI counters are counted per event row so they align with what the user can visually count in the list.
  const countRows = (predicate) => sortedLogs.reduce((acc, l) => acc + (predicate(l.eventName) ? 1 : 0), 0);

  const reloads = countRows(isPageReloadEvent);
  const tabSwitches = countRows(isTabVisibilityEvent);
  const cameraIssues = countRows(isCameraIssueEvent);
  // Flags are counted per event row (unweighted) so they match what users see in the timeline list.
  const totalFlags = sortedLogs.reduce((acc, l) => acc + (isFlagEventName(l.eventName) ? 1 : 0), 0);

  const firstLocal = sortedLogs[0]?.timestamp;
  const lastLocal = sortedLogs[sortedLogs.length - 1]?.timestamp;
  const timeRange =
    firstLocal && lastLocal
      ? `${formatDate(firstLocal, "hh:mm:ss a", { locale: enIN })} — ${formatDate(lastLocal, "hh:mm:ss a", { locale: enIN })}`
      : "";

  const score = clamp(100 - reloads * 6 - tabSwitches * 3 - cameraIssues * 12, 0, 100);

  return {
    totalQuestions,
    totalEvents,
    totalFlags,
    reloads,
    tabSwitches,
    cameraIssues,
    timeRange,
    score,
  };
}

/**
 * Groups log rows by question for the accordion list.
 * @returns {{ q: number, questionText: string|null, duration: string|null, rows: object[], flagCount: number }[]}
 */
export function buildQuestionGroups(sortedLogs, questionTextOrderMap) {
  const map = new Map();
  sortedLogs.forEach((l) => {
    const q = getQuestionGroupKey(l, questionTextOrderMap);
    if (!map.has(q)) map.set(q, []);
    map.get(q).push(l);
  });

  const groups = [...map.entries()].map(([q, rows]) => {
    const questionText =
      rows.find((r) => typeof r.questionText === "string" && r.questionText.trim())?.questionText ?? null;
    const duration = formatQuestionActivityDuration(rows);
    // Count flags per row (not by `count`) so flags never exceed the number of events shown for that question.
    const flagCount = rows.reduce((acc, r) => acc + (isFlagEventName(r.eventName) ? 1 : 0), 0);
    return { q, questionText, duration, rows, flagCount };
  });

  const withQuestion = groups.filter((g) => g.q > 0).sort((a, b) => a.q - b.q);
  const feedback = groups.filter((g) => g.q === -1);
  const unassigned = groups.filter((g) => g.q === 0);
  return [...withQuestion, ...feedback, ...unassigned];
}

function formatQuestionActivityDuration(rows) {
  const ts = rows.map((r) => new Date(r.timestamp).getTime()).filter((t) => Number.isFinite(t));
  if (ts.length < 2) return null;
  const sec = Math.round((Math.max(...ts) - Math.min(...ts)) / 1000);
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}
